<template>
  <v-dialog v-model="showDialog" persistent max-width="768px">
    <v-card>
      <v-card-title class="text-h5 grey lighten-2 d-flex justify-content-between">
        <span>{{ $t('survey') }}</span>
      </v-card-title>
      <v-card-text>
        <v-layout fill-height wrap>
          <v-flex
            :class="['form-control d-flex mb-2', { 'has-error': errors.has('messageText') }]"
            xs12
            style="flex-direction: column"
          >
            <label class="mb-1">
              {{ $t('Response Message') }}
            </label>
            <AutoResizeTextarea
              v-model="message"
              v-validate="`max:${messageMaxLength}|required`"
              name="messageText"
              :maxlength="messageMaxLength"
              style="margin: 0 0 4px"
              :data-vv-as="$t('Response Message')"
              :data-vv-name="'messageText'"
              :placeholder="$t('Response Message')"
            />
            <small style="margin-left: auto; font-size: 12px"
              >({{ messageLength }} / {{ messageMaxLength }})</small
            >
            <span v-show="errors.has('messageText')" class="text-error"
              >{{ errors.first('messageText') }}
            </span>
          </v-flex>
          <v-flex
            :class="['form-control d-flex mb-2', { 'has-error': errors.has('buttonName') }]"
            xs12
            style="flex-direction: column"
          >
            <label class="mb-1">
              {{ $t('Button Name') }}
            </label>
            <input
              v-model="button_name"
              v-validate="`max:${buttonNameMaxLength}|required`"
              type="text"
              class="form-control"
              :maxlength="buttonNameMaxLength"
              name="buttonName"
              :data-vv-as="$t('Button Name')"
              :placeholder="$t('Button Name')"
            />
            <div class="d-flex" style="justify-content: space-between">
              <span v-show="errors.has('buttonName')" class="text-error"
                >{{ errors.first('buttonName') }}
              </span>
              <small style="text-align: right; font-size: 12px"
                >({{ buttonNameLength }} / {{ buttonNameMaxLength }})</small
              >
            </div>
          </v-flex>
          <v-flex
            :class="['d-flex mb-2', { 'has-error': errors.has('survey') }]"
            xs12
            style="flex-direction: column"
          >
            <label class="mb-1">
              {{ $t('survey_select') }}
            </label>
            <v-autocomplete
              v-model="survey"
              v-validate="'required'"
              :items="surveyOptions"
              item-text="survey_name"
              item-value="id"
              return-object
              :placeholder="$t('survey_select')"
              outline
              single-line
              :data-vv-as="$t('survey')"
              name="survey"
              hide-details
              :error-messages="errors.collect('survey')"
            />
            <span v-show="errors.has('survey')" class="text-error mt-2"
              >{{ errors.first('survey') }}
            </span>
          </v-flex>
          <v-flex class="mb-4" xs12>
            <v-checkbox
              v-model="is_default"
              hide-details
              color="primary"
              :label="$t('set_default')"
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 class="text-xs-center mb-4">
            <a href="javascript:void(0);" class="btn-preview" @click="showPreview = true">
              <em class="far fa-eye" />
              {{ $t('preview') }}
            </a>
            <PreviewMessage
              v-if="showPreview"
              :messages="previewMessage"
              @close="showPreview = false"
            />
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-layout justify-center wrap>
          <v-flex shrink class="px-2">
            <v-btn outline color="grey" :disabled="isLoading" @click="handleClose">{{
              $t('cancel')
            }}</v-btn>
          </v-flex>
          <v-flex shrink class="px-2">
            <v-btn depressed color="primary" :disabled="isLoading" @click="handleSubmit">{{
              $t('Save')
            }}</v-btn>
          </v-flex>
        </v-layout>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import AutoResizeTextarea from '@/components/elements/AutoresizeTextarea'
import surveyApi from '@/api/survey'
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'
import PreviewMessage from '@/components/elements/PreviewMessage'
export default {
  components: { AutoResizeTextarea, PreviewMessage },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showDialog: this.value,
      isLoading: false,
      messageMaxLength: 640,
      buttonNameMaxLength: 20,
      surveyOptions: [],
      showPreview: false,
      is_default: false,
      message: '',
      button_name: '',
      survey: null,
      created_at: dayjs().format(),
      updated_at: dayjs().format(),
      activeIndex: null
    }
  },
  computed: {
    ...mapGetters(['selectedBot']),
    messageLength() {
      return Number(this.message.length)
    },
    buttonNameLength() {
      return Number(this.button_name.length)
    },
    previewMessage() {
      return [
        [
          {
            text: this.message,
            buttons: [
              {
                title: this.button_name
              }
            ],
            type: 'button'
          }
        ]
      ]
    }
  },
  watch: {
    value(isShow) {
      this.showDialog = isShow
    }
  },
  mounted() {
    this.handleFetchSurvey()
  },
  methods: {
    handleClose() {
      this.showDialog = false
      this.showPreview = false
      this.is_default = false
      this.message = ''
      this.button_name = ''
      this.survey = null
      this.created_at = dayjs().format()
      this.updated_at = dayjs().format()
      this.activeIndex = null
      this.$validator.reset()
      this.$emit('input', false)
    },
    async handleFetchSurvey() {
      try {
        this.isLoading = true
        const response = await surveyApi.getAll({
          params: {
            page_id: this.selectedBot.pageid,
            is_used: true
          }
        })
        if (response.status === 200) {
          this.surveyOptions = response.data.data
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit() {
      try {
        this.$validator.validateAll().then((result) => {
          if (!result) {
            this.goToErrorInput()
            return false
          }
          const payload = {
            is_default: this.is_default,
            message: this.message,
            button_name: this.button_name,
            survey_id: this.survey.id,
            created_at: this.created_at,
            updated_at: dayjs().format()
          }
          this.$emit('submit', payload, this.activeIndex)
          this.handleClose()
        })
      } catch (error) {
        console.error(error)
      }
    },
    handleInitData(data, index) {
      this.is_default = data.is_default
      this.message = data.message
      this.button_name = data.button_name
      this.survey = this.surveyOptions.find((item) => Number(item.id) === Number(data.survey_id))
      this.created_at = data.created_at
      this.showDialog = true
      this.activeIndex = index
    }
  }
}
</script>
<style lang="scss" scoped>
.form__wrapper {
  border-radius: 10px;
  background: #eee;
  padding: 16px;
  display: flex;
  flex-direction: column;
}
.text-error {
  color: red;
  font-size: 12px;
}
.theme--light.v-text-field--outline > .v-input__control > .v-input__slot {
  border-color: red;
}
</style>



// WEBPACK FOOTER //
// src/components/view/ticket3/ticket-management/Setting/CustomerSupportService/SurveyFormDialog.vue